import { AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import React from 'react';
import type { ValidationError } from '../../lib/api-client';

interface ValidationResultDisplayProps {
	errors: ValidationError[];
	isValidating?: boolean;
	onDismiss?: () => void;
	className?: string;
}

/**
 * Component to display validation results with proper styling and icons
 */
export const ValidationResultDisplay = React.memo(function ValidationResultDisplay({
	errors,
	isValidating = false,
	onDismiss,
	className = '',
}: ValidationResultDisplayProps) {
	if (isValidating) {
		return (
			<div
				className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 ${className}`}
			>
				<div className="flex items-center gap-3">
					<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
					<span className="text-sm text-blue-700 dark:text-blue-300">
						Validating node configuration...
					</span>
				</div>
			</div>
		);
	}

	if (errors.length === 0) {
		return (
			<div
				className={`bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 ${className}`}
			>
				<div className="flex items-center gap-3">
					<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
					<span className="text-sm text-green-700 dark:text-green-300">
						Node configuration is valid
					</span>
				</div>
			</div>
		);
	}

	const getSeverityIcon = (severity: ValidationError['severity']) => {
		switch (severity) {
			case 'error':
				return <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />;
			case 'warning':
				return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
			case 'info':
				return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
			default:
				return <Info className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
		}
	};

	const getSeverityStyles = (severity: ValidationError['severity']) => {
		switch (severity) {
			case 'error':
				return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300';
			case 'warning':
				return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300';
			case 'info':
				return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300';
			default:
				return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300';
		}
	};

	return (
		<div
			className={`border rounded-lg p-4 ${getSeverityStyles(
				errors[0]?.severity || 'info'
			)} ${className}`}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="flex items-start gap-3 flex-1">
					{getSeverityIcon(errors[0]?.severity || 'info')}
					<div className="flex-1 space-y-2">
						{errors.map((error, index) => (
							<div key={index} className="space-y-1">
								<div className="text-sm font-medium">
									{error.field && error.field !== 'validation' && (
										<span className="font-mono text-xs bg-white/50 dark:bg-black/20 px-1 py-0.5 rounded mr-2">
											{error.field}
										</span>
									)}
									{error.message}
								</div>
								{error.suggestion && (
									<div className="text-xs opacity-80">💡 {error.suggestion}</div>
								)}
							</div>
						))}
					</div>
				</div>
				{onDismiss && (
					<button
						onClick={onDismiss}
						className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>
		</div>
	);
});
